import React, { useEffect } from 'react';

const Services = () => {
  // Animation logic for numbers
  useEffect(() => {
    const animateValue = (element, start, end, duration) => {
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        element.textContent = value + (element.dataset.suffix || '');
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const statNumber = entry.target.querySelector('.stat-number');
          if (statNumber && !statNumber.classList.contains('animated')) {
            statNumber.classList.add('animated');
            const text = statNumber.textContent;
            const num = parseInt(text.replace(/\D/g, ''));
            const suffix = text.replace(/[0-9]/g, '');
            statNumber.dataset.suffix = suffix;
            statNumber.textContent = '0' + suffix;
            setTimeout(() => {
              animateValue(statNumber, 0, num, 2000);
            }, 200);
          }
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    // Delay slightly to ensure elements exist
    setTimeout(() => {
        document.querySelectorAll('.stat-item').forEach(item => observer.observe(item));
    }, 500);
  }, []);

  // Add tilt effect to portfolio cards
  useEffect(() => {
    const handleMouseMove = (e, card) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 10;
      const rotateY = (centerX - x) / 10;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-12px) scale(1.02)`;
    };

    const handleMouseLeave = (card) => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
    };

    setTimeout(() => {
      const portfolioCards = document.querySelectorAll('.portfolio-glass-card');
      portfolioCards.forEach(card => {
        card.addEventListener('mousemove', (e) => handleMouseMove(e, card));
        card.addEventListener('mouseleave', () => handleMouseLeave(card));
      });
    }, 100);
  }, []);

  return (
    <>
      {/* Stats Section */}
      <div className="stats-section">
        <div className="stats-glass">
          <div className="stats-grid">
            <div className="stat-item fade-in">
              <div className="stat-number">5+</div>
              <div className="stat-label">Experience</div>
            </div>
            <div className="stat-item fade-in">
              <div className="stat-number">100+</div>
              <div className="stat-label">Projects covered</div>
            </div>
            <div className="stat-item fade-in">
              <div className="stat-number">100+</div>
              <div className="stat-label">Happy customers</div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section id="services">
        <div className="section-header">
          <h2 className="section-title">Our Premium Services</h2>
          <p className="section-subtitle">Comprehensive glassmorphic solutions tailored to your business needs</p>
        </div>
        <div className="services-grid">
          <div className="service-glass-card fade-in">
            <div className="service-icon-wrapper">💻</div>
            <h3>Web Development</h3>
            <p>Create stunning, responsive websites with modern glassmorphic designs that captivate your audience.</p>
            <ul className="service-features">
              <li>Custom Website Design</li>
              <li>E-Commerce Solutions</li>
              <li>Progressive Web Apps</li>
              <li>API Integration</li>
            </ul>
          </div>
          <div className="service-glass-card fade-in">
            <div className="service-icon-wrapper">📱</div>
            <h3>Mobile App Development</h3>
            <p>Native and cross-platform mobile applications with sleek, futuristic interfaces.</p>
            <ul className="service-features">
              <li>iOS & Android Apps</li>
              <li>React Native Solutions</li>
              <li>App Store Optimization</li>
              <li>24/7 Maintenance Support</li>
            </ul>
          </div>
          <div className="service-glass-card fade-in">
            <div className="service-icon-wrapper">🎨</div>
            <h3>UI/UX Design</h3>
            <p>Beautiful, intuitive glassmorphic designs that enhance user experience and brand identity.</p>
            <ul className="service-features">
              <li>User Research & Testing</li>
              <li>Glassmorphic UI Design</li>
              <li>Interactive Prototypes</li>
              <li>Design Systems</li>
            </ul>
          </div>
          <div className="service-glass-card fade-in">
            <div className="service-icon-wrapper">⚙️</div>
            <h3>Custom Software</h3>
            <p>Bespoke software solutions with futuristic interfaces designed for your unique needs.</p>
            <ul className="service-features">
              <li>Enterprise Solutions</li>
              <li>Cloud Integration</li>
              <li>Database Management</li>
              <li>System Automation</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features">
        <div className="features-section fade-in">
          <div className="section-header">
            <h2 className="section-title">Why Choose Zentak</h2>
            <p className="section-subtitle">Innovation, expertise, and futuristic design in every project</p>
          </div>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <h4>Lightning Fast</h4>
              <p>Optimized performance and blazing load times for the best user experience</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔒</div>
              <h4>Secure & Reliable</h4>
              <p>Industry-standard security with encrypted data protection</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📈</div>
              <h4>Scalable Solutions</h4>
              <p>Built to grow seamlessly with your business expansion</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🎯</div>
              <h4>Agile Methodology</h4>
              <p>Flexible development with regular updates and iterations</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💬</div>
              <h4>24/7 Support</h4>
              <p>Always available to help when you need us most</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🧠</div>
              <h4>AI-Ready Architecture</h4>
              <p>Built to integrate modern AI capabilities securely and efficiently</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">☁️</div>
              <h4>Cloud-Native</h4>
              <p>Deployments optimized for modern cloud platforms and resilience</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💰</div>
              <h4>Transparent Pricing</h4>
              <p>Clear estimates and no hidden fees tailored to your scope</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Portfolio section removed as requested */}

      {/* CTA Section */}
      <section>
        <div className="cta-section fade-in">
          <div className="cta-content">
            <h2>Ready To Build The Future?</h2>
            <p>Let's create something extraordinary with glassmorphic design</p>
            <a href="#contact" className="glass-btn glass-btn-primary">
              <span>Get Started Now</span>
              <span>✨</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;